import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Bot, CheckSquare, Library, Loader2, RefreshCw, Square, X } from 'lucide-react';
import type { GbuKiVorschlag, GbuVoll, Kataster, SifaArbeitsbereich } from '../../../services/sifa/gbuTypes';
import { GBU_ERSATZ_HINWEIS, baueGbuKiKontext, ersatzVorschlag, fordereGbuVorschlagAn, ohneDubletten } from '../../../services/sifa/gbuKi';
import { GbuKonfliktError, uebernehmeKiAuswahl } from '../../../services/sifa/gbuService';
import { aktiveGefaehrdungen } from '../../../services/sifa/gbuLogic';
import { faktorLabel } from '../../../services/sifa/kataloge/gefaehrdungsfaktoren';
import { psaLabel } from '../../../services/sifa/kataloge/psaKatalog';
import { anlassLabel } from '../../../services/sifa/kataloge/arbmedvvAnlaesse';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { RisikoBadge } from './RisikoWahl';
import {
  MASSNAHME_ART_TON,
  VERWEIS_LABEL,
  baueKiAuswahl,
  kiKey,
  kiLeerText,
  kopfAus,
  kopfVorschlagZeilen,
  schalteKiHaken,
  setzeKiGefaehrdung,
  vorauswahlKi,
  zaehleKiAuswahl,
} from './gbuModulLogic';

interface Props {
  voll: GbuVoll;
  bereich: SifaArbeitsbereich | null;
  kataster: Kataster;
  branche: string | null;
  /**
   * Fertiger Vorschlag (aus der KI-Pruefung) statt einer neuen KI-Anfrage —
   * er laeuft so ueber denselben Pruefbildschirm und wird nie ungesehen
   * uebernommen.
   */
  vorgabe?: GbuKiVorschlag | null;
  onClose: () => void;
  /** Nach der Uebernahme: der Editor laedt neu. */
  onUebernommen: (text: string) => void;
  /** Konflikt der optimistischen Sperre — der Editor zeigt „neu laden". */
  onKonflikt: () => void;
  /** Uebernahme brach mittendrin ab: der Editor schliesst, laedt neu und zeigt den Text. */
  onTeilfehler: (text: string) => void;
}

/** Kaestchen eines Hakens; gesperrt = nie waehlbar (Kopf-Feld schon gefuellt). */
const Haken: React.FC<{ an: boolean; gesperrt?: boolean }> = ({ an, gesperrt }) => (
  an && !gesperrt ? <CheckSquare className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> : <Square className={`w-4 h-4 shrink-0 mt-0.5 ${gesperrt ? 'text-slate-200' : 'text-slate-300'}`} />
);

type Stand =
  | { art: 'laeuft' }
  | { art: 'fertig'; vorschlag: GbuKiVorschlag; ersatz: boolean; fehlerText: string | null; ausLeer?: boolean };

/**
 * Pruefbildschirm des KI-Vorschlags (K1), Muster WizardStepGefaehrdungen:
 * Haken je Gefaehrdung, Massnahme, Vorsorge und Kopf-Feld. Vor „Übernehmen"
 * wird nichts geschrieben; danach fuegt uebernehmeKiAuswahl nur ein und
 * ueberschreibt nie.
 *
 * KI-Zeilen sind vorangehakt, Ersatz-Zeilen (KI nicht erreichbar) nicht —
 * ein Ausfall sieht nie wie ein fertiger Vorschlag aus. Bereits erfasste
 * (aktive) Gefaehrdungen blendet der Dublettenabgleich in gbuKi aus. Eine
 * LEERE KI-Antwort ist kein Befund: dann steht das so da, und der
 * Ersatzvorschlag aus dem Katalog laesst sich holen.
 *
 * Mit `vorgabe` (ein Vorschlag aus der KI-Pruefung) entfaellt die Anfrage.
 *
 * Die Regelwerkspruefung (verworfene Vorschriften) laeuft ueber den globalen
 * Dialog KiRegelwerkHinweis, bevor die Antwort hier ankommt.
 */
export const GbuKiDialog: React.FC<Props> = ({ voll, bereich, kataster, branche, vorgabe = null, onClose, onUebernommen, onKonflikt, onTeilfehler }) => {
  const [stand, setStand] = useState<Stand>({ art: 'laeuft' });
  const [haken, setHaken] = useState<Set<string>>(new Set());
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [lauf, setLauf] = useState(0);
  const speichertRef = useRef(false);

  const ist = useMemo(() => kopfAus(voll.gbu), [voll.gbu]);
  const aktive = useMemo(() => aktiveGefaehrdungen(voll), [voll]);
  const kontext = useMemo(() => baueGbuKiKontext({
    bereich,
    arbeitsmittel: kataster.arbeitsmittel.filter((a) => a.aktiv),
    stoffe: kataster.stoffe.filter((s) => s.aktiv),
    branche,
    vorhandene: aktive,
  }), [bereich, kataster.arbeitsmittel, kataster.stoffe, branche, aktive]);

  // Ein Lauf je Oeffnen bzw. „Erneut anfragen". Wirft nie (gbuKi); eine spaete
  // Antwort nach dem Schliessen wird verworfen. Mit `vorgabe` keine Anfrage.
  useEffect(() => {
    let aktiv = true;
    setFehler(null);
    if (vorgabe) {
      const v = ohneDubletten(vorgabe, aktive);
      setStand({ art: 'fertig', vorschlag: v, ersatz: false, fehlerText: null });
      setHaken(vorauswahlKi(v, ist));
      return () => { aktiv = false; };
    }
    setStand({ art: 'laeuft' });
    void fordereGbuVorschlagAn(kontext).then((erg) => {
      if (!aktiv) return;
      if (erg.status === 'ok') {
        setStand({ art: 'fertig', vorschlag: erg.vorschlag, ersatz: false, fehlerText: null });
        setHaken(vorauswahlKi(erg.vorschlag, ist));
      } else {
        setStand({ art: 'fertig', vorschlag: erg.ersatz, ersatz: true, fehlerText: erg.text });
        setHaken(vorauswahlKi(erg.ersatz, ist));
      }
    });
    return () => { aktiv = false; };
    // Bewusst nur `lauf`: ein neuer Kontext (Neuladen im Hintergrund) startet keinen zweiten KI-Aufruf.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lauf]);

  /** Leere KI-Antwort: den Ersatzvorschlag aus dem Katalog zeigen — nichts vorangehakt. */
  const ersatzZeigen = () => {
    const v = ohneDubletten(ersatzVorschlag(kontext), kontext.vorhandene);
    setStand({ art: 'fertig', vorschlag: v, ersatz: true, fehlerText: 'Die KI hat keine Gefährdungen geliefert.', ausLeer: true });
    setHaken(vorauswahlKi(v, ist));
  };

  const vorschlag = stand.art === 'fertig' ? stand.vorschlag : null;
  const auswahl = useMemo(() => (vorschlag ? baueKiAuswahl(vorschlag, haken, ist) : null), [vorschlag, haken, ist]);
  const anzahl = auswahl ? zaehleKiAuswahl(auswahl) : 0;
  const kopfZeilen = useMemo(() => (vorschlag ? kopfVorschlagZeilen(vorschlag.kopf, ist) : []), [vorschlag, ist]);

  const name = (liste: Array<{ id: string; bezeichnung: string }>, id: string) => liste.find((x) => x.id === id)?.bezeichnung;

  const uebernehmen = async () => {
    if (!auswahl || anzahl === 0 || speichertRef.current) return;
    speichertRef.current = true;
    setSpeichert(true);
    setFehler(null);
    try {
      const erg = await uebernehmeKiAuswahl(voll.gbu, auswahl);
      onUebernommen(
        `${erg.gefaehrdungen.length} Gefährdung(en), ${erg.massnahmen.length} Maßnahme(n) und ${erg.vorsorge.length} Vorsorgeanlass/-anlässe übernommen` +
        (Object.keys(auswahl.kopf).length ? ', Kopf ergänzt.' : '.'),
      );
    } catch (e) {
      if (e instanceof GbuKonfliktError) { onKonflikt(); return; }
      // Teilweise angelegt: zu und neu laden — ein zweiter Klick hier legte das Angekommene doppelt an.
      onTeilfehler(`Übernahme abgebrochen: ${rechtFehlerOder(e)} Bereits Übernommenes bleibt stehen — der Stand wurde neu geladen, bitte prüfen.`);
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Bot className="w-4 h-4 text-violet-600" /> KI-Vorschlag prüfen</h3>
            <p className="text-xs text-slate-500 truncate">Nr. {voll.gbu.nummer} — {bereich?.bezeichnung || voll.gbu.titel}</p>
          </div>
          <button onClick={onClose} disabled={speichert} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {stand.art === 'laeuft' ? (
            <div className="p-10 flex flex-col items-center gap-3 text-sm text-slate-600">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              Die KI beurteilt „{bereich?.bezeichnung || voll.gbu.titel}" — das dauert einige Sekunden.
            </div>
          ) : (
            <>
              {stand.ersatz && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                  <div className="space-y-1">
                    <p className="font-bold">{stand.fehlerText}</p>
                    <p>{stand.ausLeer ? "Diese Vorschläge stammen aus Kataster und Faktorenkatalog — allgemeine Beispiele, keine Beurteilung dieser Tätigkeit. Nur übernehmen, was zutrifft, und W/S prüfen." : GBU_ERSATZ_HINWEIS}</p>
                  </div>
                </div>
              )}
              {stand.vorschlag.abgeschnitten && (
                <p className="p-2.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg">
                  Die Antwort war zu lang und wurde gekürzt — die Liste ist evtl. unvollständig. Nach der Übernahme „KI-Vorschlag" erneut anfragen.
                </p>
              )}
              {stand.vorschlag.hinweise.length > 0 && (
                <ul className="text-xs text-slate-600 space-y-0.5 list-disc pl-4">
                  {stand.vorschlag.hinweise.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              )}
              <p className="text-[11px] text-slate-500">Bereits erfasste Gefährdungen sind ausgeblendet. Übernommen wird nur, was angehakt ist; bestehende Zeilen bleiben unverändert.</p>

              {stand.vorschlag.gefaehrdungen.length === 0 && kopfZeilen.length === 0 && (
                <div className={`p-5 text-center text-sm rounded-xl space-y-3 ${!stand.ersatz && aktive.length === 0 ? 'bg-amber-50 border border-amber-300 text-amber-900' : 'bg-slate-50 text-slate-600'}`}>
                  <p>{vorgabe ? 'Dieser Vorschlag ist schon erfasst.' : kiLeerText(aktive.length, stand.ersatz)}</p>
                  {!stand.ersatz && !vorgabe && (
                    <button onClick={ersatzZeigen} className="min-h-[44px] px-4 inline-flex items-center gap-2 text-sm font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl">
                      <Library className="w-4 h-4" /> Ersatzvorschlag aus dem Katalog
                    </button>
                  )}
                </div>
              )}

              {stand.vorschlag.gefaehrdungen.map((g) => {
                const gAn = haken.has(kiKey.g(g.tempId));
                const bezug = [
                  ...g.arbeitsmittelIds.map((id) => name(kataster.arbeitsmittel, id)),
                  ...g.stoffIds.map((id) => name(kataster.stoffe, id)),
                ].filter(Boolean).join(', ');
                return (
                  <div key={g.tempId} className={`border rounded-xl p-3 space-y-2 ${gAn ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}>
                    <button type="button" onClick={() => setHaken((h) => setzeKiGefaehrdung(h, g, !gAn))} className="w-full text-left flex items-start gap-2">
                      <Haken an={gAn} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold uppercase text-slate-500">{faktorLabel(g.faktor)}{g.quelle === 'ersatz' ? ' · aus Katalog' : ''}</p>
                        <p className="text-sm font-semibold text-slate-800">{g.gefaehrdung}</p>
                        {g.folgen && <p className="text-xs text-slate-500">Folgen: {g.folgen}</p>}
                        {bezug && <p className="text-xs text-slate-500">Bezug: {bezug}</p>}
                        {g.vorschriften.length > 0 && <p className="text-[11px] text-slate-500">{g.vorschriften.join(' · ')}</p>}
                      </div>
                      <div className="shrink-0 flex items-center gap-1">
                        <RisikoBadge w={g.wVor} s={g.sVor} titel="vorher" />
                        <span className="text-slate-400 text-xs">→</span>
                        <RisikoBadge w={g.wNach} s={g.sNach} titel="nachher" />
                      </div>
                    </button>
                    {(g.massnahmen.length > 0 || g.vorsorge.length > 0) && (
                      <div className={`pl-6 space-y-1 ${gAn ? '' : 'opacity-50'}`}>
                        {g.massnahmen.map((m) => {
                          const key = kiKey.m(g.tempId, m.tempId);
                          return (
                            <button key={key} type="button" disabled={!gAn} onClick={() => setHaken((h) => schalteKiHaken(h, key))} className="w-full text-left flex items-start gap-2 py-0.5 disabled:cursor-not-allowed">
                              <Haken an={gAn && haken.has(key)} />
                              <span className={`shrink-0 px-1.5 text-[11px] font-black rounded ${MASSNAHME_ART_TON[m.art]}`}>{m.art}</span>
                              <span className="text-xs text-slate-700">
                                {m.text}{m.psaCode ? ` — ${psaLabel(m.psaCode)}` : ''}{m.verweis ? ` (${VERWEIS_LABEL[m.verweis]})` : ''}
                              </span>
                            </button>
                          );
                        })}
                        {g.vorsorge.map((v) => {
                          const key = kiKey.v(g.tempId, v.tempId);
                          return (
                            <button key={key} type="button" disabled={!gAn} onClick={() => setHaken((h) => schalteKiHaken(h, key))} className="w-full text-left flex items-start gap-2 py-0.5 disabled:cursor-not-allowed">
                              <Haken an={gAn && haken.has(key)} />
                              <span className="shrink-0 px-1.5 text-[11px] font-black rounded text-teal-800 bg-teal-100">V</span>
                              <span className="text-xs text-slate-700">Vorsorge: {anlassLabel(v.anlassCode)}{v.begruendung ? ` — ${v.begruendung}` : ''}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {kopfZeilen.length > 0 && (
                <div className="border border-slate-200 rounded-xl p-3 space-y-1">
                  <p className="text-[11px] font-black uppercase tracking-wide text-slate-500 mb-1">Kopf und Abschnitte 5/6 — nur Ergänzungen</p>
                  {kopfZeilen.map((z) => (
                    <button key={z.key} type="button" disabled={z.gesperrt} onClick={() => setHaken((h) => schalteKiHaken(h, z.key))} className="w-full text-left flex items-start gap-2 py-0.5 disabled:cursor-not-allowed">
                      <Haken an={haken.has(z.key)} gesperrt={z.gesperrt} />
                      <span className={`text-xs ${z.gesperrt ? 'text-slate-400' : 'text-slate-700'}`}>
                        <b>{z.label}:</b> {z.wert}{z.gesperrt ? ' — schon vorhanden bzw. Feld gefüllt, wird nicht überschrieben' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {fehler && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{fehler}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex flex-wrap justify-between gap-2 shrink-0">
          {vorgabe ? <span /> : (
            <button onClick={() => setLauf((n) => n + 1)} disabled={stand.art === 'laeuft' || speichert} className="min-h-[44px] px-3 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">
              <RefreshCw className="w-4 h-4" /> Erneut anfragen
            </button>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} disabled={speichert} className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">Abbrechen</button>
            <button onClick={() => void uebernehmen()} disabled={speichert || anzahl === 0 || stand.art === 'laeuft'} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-40">
              {speichert && <Loader2 className="w-4 h-4 animate-spin" />} Übernehmen ({anzahl})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
